import { Box, Text } from 'grommet';
import React, { useContext, useState } from 'react';
import { useRouter } from 'next/router';
import useSWR from 'swr';
import { handleErrors } from '../../../utils/errors';
import { useBackgroundSWR } from '../../../utils/useBackgroundSWR';
import { NotificationsContext } from '../../NotificationsProvider';
import AlertBanner from '../../AlertBanner';
import {Anchor} from "grommet";
import ChartPanel from '../../charts/ChartPanel';
import DashboardStrip from './DashboardStrip';
import TopCustomers from '../../charts/TopCustomers';
import FollowUpFirst from '../../charts/FollowUpFirst';
import GoneQuiet from '../../charts/GoneQuiet';
import NotificationBanner from '../../NotificationBanner';
import {useSession} from "next-auth/react";
import styled from 'styled-components';

const CardGrid = styled.div`
    display: grid;
    gap: 20px;
    margin: 12px 0 20px;
    grid-auto-rows: min-content;
    /* Follows the space the sidebar leaves, not the viewport */
    grid-template-columns: repeat(auto-fit, minmax(min(340px, 100%), 1fr));
`;

// Takes no gap when the card has nothing to show
const FullWidth = styled.div`
    grid-column: 1 / -1;

    &:empty { display: none; }
`;

const Dashboard = () => {
    const [reportDate, setReportDate] = useState();
    const {data: session} = useSession();
    const {company} = session || {};
    const {currency} = company || {};
    const notificationCtx = useContext(NotificationsContext);
    const router = useRouter();

    const fetcher = (url) => fetch(url)
        .then(handleErrors)
        .catch(notificationCtx.showNotification);

    // fetch balances to display in dashboard aging balance graph
    const {data: dashboardData, error: dashboardError} = useSWR(
        `/api/company/dashboard?${reportDate ? `report_date=${reportDate.toISOString()}&` : ''}`,
        fetcher,
        {
            revalidateOnFocus: false,
            // Changing the "As of" date keeps the last chart up instead of flashing a skeleton
            keepPreviousData: true,
        }
    );

    const {data: unknownPaymentsData, error: unknownPaymentsError} = useBackgroundSWR(
        `/api/company/unknown-payments`,
        {
            revalidateOnFocus: false,
        }
    );

    const { data: customerSummaryData, error: customerSummaryDataError} = useBackgroundSWR(
        `/api/company/customer-summary`,
        {
            revalidateOnFocus: false,
        }
    );
    const pendingApplications = customerSummaryData?.total_applications ?? 0;

    const {data: notifications, error: notificationsError, mutate: mutateNotifications} = useBackgroundSWR(
        `/api/company/notifications`,
        {
            revalidateOnFocus: false,
        }
    );

    const {data: monthlySales, error: monthlySalesError} = useSWR(
        `/api/reports/monthly-sales`,
        fetcher,
        {
            revalidateOnFocus: false,
        }
    );

    const {data: averagePaymentPeriod, error: averagePaymentPeriodError} = useSWR(
        `/api/reports/average-payment`,
        fetcher,
        {
            revalidateOnFocus: false,
        }
    );

    return (
        <Box width={{max: '2020px'}}>
            {unknownPaymentsData && unknownPaymentsData?.unknown_payments_count > 0 && (
                <AlertBanner
                    message={`You have ${unknownPaymentsData?.unknown_payments_count} transaction${unknownPaymentsData?.unknown_payments_count > 1 ? 's' :''} that ${unknownPaymentsData?.unknown_payments_count > 1 ? 'are' :'is'} not associated with an invoice`}
                    ctaText='Fix now'
                    onClick={() => router.push('/transactions')}
                />
            )}
            {pendingApplications > 0 && (
                <AlertBanner
                    message={`You have ${pendingApplications} customer application${pendingApplications > 1 ? 's' :''} that need${pendingApplications > 1 ? '' :'s'} review`}
                    ctaText='Review'
                    onClick={() => router.push('/customers/applications')}
                />
            )}
            {notifications && notifications.notifications.length > 0 && (
                notifications.notifications.map((notification, index) => {
                    const message = notification.details?.length > 0
                        ? <Box direction="column" gap="xxsmall">
                            {notification.details.map((detail, i) => (
                                <Text key={i} wordBreak="break-word" weight={450}>
                                    <Anchor href={detail.invoice_token ? `/invoices/${detail.invoice_token}` : `/draft-invoices/${detail.order_id}`}>
                                        {detail.order_identifier}
                                    </Anchor>
                                    {detail.customer_name && ` (${detail.customer_name})`}
                                    {' — '}{detail.reason}
                                </Text>
                            ))}
                        </Box>
                        : notification.message;
                    return (
                        <NotificationBanner
                            key={index}
                            notification={{...notification, message}}
                            onDismiss={mutateNotifications}
                        />
                    );
                })
            )}
            <CardGrid>
                <FullWidth>
                    <DashboardStrip
                        dashboardData={dashboardData}
                        monthlySales={monthlySales}
                        averagePaymentPeriod={averagePaymentPeriod}
                        currency={currency}
                        reportDate={reportDate}
                        setReportDate={setReportDate}
                    />
                </FullWidth>
                {/* One frame for the three, so they line up however long each one runs */}
                <FullWidth>
                    <ChartPanel>
                        {/* A response without data has no period to ask about */}
                        {monthlySales?.data?.length > 0 && (
                            <TopCustomers
                                months={monthlySales.data.map((row) => row.month)}
                                currency={currency}
                                hasActivity={monthlySales.data.some((row) => row.sales_amount || row.payment_amount)}
                            />
                        )}
                        <GoneQuiet currency={currency} />
                        <FollowUpFirst currency={currency} />
                    </ChartPanel>
                </FullWidth>
            </CardGrid>
        </Box>
    );
}


export default Dashboard;
